import { Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import LoadingSpinner from "./LoadingSpinner";
import type { ReactNode } from "react";

interface AdminRouteProps {
  children: ReactNode;
}

/**
 * Guards admin routes.
 * - Loading → spinner
 * - Unauthenticated → /login
 * - No admin panel access → /login
 */
export default function AdminRoute({ children }: AdminRouteProps) {
  const { currentUser, hasAdminPanelAccess, loading } = useAuth();

  if (loading) return <LoadingSpinner size="lg" fullScreen />;
  if (!currentUser) return <Navigate to="/login" replace />;
  if (!hasAdminPanelAccess) return <Navigate to="/login" replace />;

  return <>{children}</>;
}

/**
 * Wraps public routes that staff should never land on.
 * Unauthenticated and regular users pass through; staff (non-root) are sent to /admin.
 * Use this in App.tsx instead of PrivateRoute when the page must stay publicly accessible.
 */
export function StaffRedirect({ children }: AdminRouteProps) {
  const { userProfile, isStaff, isRoot, loading } = useAuth();

  if (!loading && userProfile && isStaff && !isRoot) {
    return <Navigate to="/admin" replace />;
  }

  return <>{children}</>;
}
